import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Badge,
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowRight16Regular,
  CheckmarkCircle48Filled,
  Open16Regular,
  People16Regular,
} from '@fluentui/react-icons'
import { useDemands, useTransitionDemand, useUpdateDemand } from '../hooks/useDemands'
import { apiErrorMessage } from '../lib/apiError'
import { parseStageWarnings, type StageWarnings } from '../lib/stageWarnings'
import { StageWarningDialog } from '../components/StageWarningDialog'
import type { Demand, DemandTransitionInput } from '../types/demand'
import { stageLabel } from '../lib/labels'
import { ErrorCard } from '../components/ErrorCard'
import { useToast } from '../components/ToastProvider'
import { QuadrantPicker, canonicalizeTier } from '../components/QuadrantPicker'
import type { StoredPriorityTierKey } from '../components/QuadrantPicker'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import {
  isOverdueDecision,
  isPostedWithoutApplications,
  isStalePosting,
} from '../lib/faWorkspaceMetrics'
import { daysInStage } from '../lib/demandSla'
import { activatable } from '../lib/activatable'

/**
 * Council Decision-Queue Triage — mockup §4889-5141.
 *
 * Triage inbox for Council members. Three card flavours surface:
 *   1. New intake — scoping demands missing a priority bucket.
 *      The card offers the 2x2 priority quadrant + rationale, then
 *      "Post for Applications" (transition → resourcing) or
 *      "Not Prioritized" (transition → parking).
 *   2. Awaiting priority — triaging demands that already have a
 *      priority_tier_override set. Same action set as new intake but
 *      the picker comes pre-selected.
 *   3. Posted · no apps yet — resourcing demands sitting
 *      >14 days with no applications. Council can invite specific
 *      talents from the directory or shelve the demand.
 *
 * Filter chips at the top show per-bucket counts. The card body
 * surfaces issue_problem / target_user / focus area for at-a-glance
 * triage. An empty state celebrates a clean queue.
 */

/* ─── Filter model ─────────────────────────────────────────────── */

type TriageFilter = 'all' | 'overdue' | 'new_intake' | 'awaiting_priority' | 'open_no_apps'

/* ─── Helpers ──────────────────────────────────────────────────── */

function normalizeTriageFilter(value: string | null): TriageFilter {
  if (
    value === 'overdue' ||
    value === 'new_intake' ||
    value === 'awaiting_priority' ||
    value === 'open_no_apps'
  ) {
    return value
  }
  return 'all'
}

function isNewIntake(d: Demand): boolean {
  // Scoping = still being understood, not yet prioritized.
  return d.stage === 'scoping'
}

function isAwaitingPriority(d: Demand): boolean {
  // Triaging = under prioritization.
  return d.stage === 'triaging'
}

function isOpenNoApps(d: Demand): boolean {
  return isPostedWithoutApplications(d) && isStalePosting(d)
}

function urgencyForDays(days: number): 'overdue' | 'due' | 'normal' {
  if (days > 14) return 'overdue'
  if (days > 7) return 'due'
  return 'normal'
}

function focusAreaLabel(code: string): string {
  // The substrate doesn't ship a name lookup yet — show the raw code.
  // FA-aware label resolution will plug in once useFocusAreas wires
  // through the page (out of scope for this card).
  return code
}

/**
 * Render someone's name as 1-2 letter initials for an avatar bubble.
 * Mirrors the helper used in FAWorkspaceOverview / mockup §5063.
 */
function initials(name: string | null | undefined): string {
  if (!name) return '?'
  return (
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  )
}

// The intake-completeness heuristic that lived here (a byte-copy of the one
// on DemandDetail — one of #998's three competing definitions) is gone: the
// readiness report on the demand page is the one surface, and it is the
// expectations engine itself, so the number shown IS the 409 the next move
// would raise (#1207).

/**
 * Stub similarity score — overlap of skill sets / max(|a|,|b|).
 * Lightweight and good enough for "did anything else like this come
 * through recently". The proper match engine lives in the search
 * service; this card just needs a number for orientation.
 */
function similarityScore(a: Demand, b: Demand): number {
  const aSkills = new Set((a.skills ?? []).map((s) => s.toLowerCase()))
  const bSkills = new Set((b.skills ?? []).map((s) => s.toLowerCase()))
  if (aSkills.size === 0 || bSkills.size === 0) return 0
  let overlap = 0
  for (const s of aSkills) if (bSkills.has(s)) overlap += 1
  return Math.round((overlap / Math.max(aSkills.size, bSkills.size)) * 100)
}

interface SimilarDemand {
  id: number
  demand_id: string | null
  title: string
  stage: Demand['stage']
  match: number
}

/**
 * Pick 0-3 most-similar demands from the pool (excluding the focus
 * demand itself). Match % must be above a small floor to avoid showing
 * zero-overlap noise.
 */
function findSimilarDemands(target: Demand, pool: Demand[]): SimilarDemand[] {
  const ranked: SimilarDemand[] = []
  for (const d of pool) {
    if (d.id === target.id) continue
    const m = similarityScore(target, d)
    if (m <= 0) continue
    ranked.push({
      id: d.id,
      demand_id: d.demand_id ?? null,
      title: d.title,
      stage: d.stage,
      match: m,
    })
  }
  ranked.sort((a, b) => b.match - a.match)
  return ranked.slice(0, 3)
}


/* ─── Styles ───────────────────────────────────────────────────── */

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxWidth: '1100px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingVerticalM,
  },
  headerTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    flex: 1,
    minWidth: '260px',
  },
  progressRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginTop: tokens.spacingVerticalS,
    maxWidth: '420px',
  },
  progressLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  progressBar: {
    height: '6px',
    background: tokens.colorNeutralBackground4,
    borderRadius: tokens.borderRadiusCircular,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: tokens.colorBrandBackground,
    transitionProperty: 'width',
    transitionDuration: '200ms',
  },
  stats: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '64px',
  },
  statNum: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: 1,
    color: tokens.colorNeutralForeground1,
  },
  statNumDanger: {
    color: tokens.colorPaletteRedForeground1,
  },
  statNumWarning: {
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  statLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXXS,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusCircular,
    fontSize: tokens.fontSizeBase200,
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    fontFamily: 'inherit',
    transitionProperty: 'background, border-color, color',
    transitionDuration: '120ms',
    ':hover': {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
    },
  },
  chipActive: {
    background: tokens.colorBrandBackground,
    borderTopColor: tokens.colorBrandBackground,
    borderRightColor: tokens.colorBrandBackground,
    borderBottomColor: tokens.colorBrandBackground,
    borderLeftColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  chipCount: {
    background: 'rgba(0, 0, 0, 0.08)',
    borderRadius: tokens.borderRadiusCircular,
    padding: '0 8px',
    minWidth: '20px',
    textAlign: 'center',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
  },
  card: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
    position: 'relative',
    boxShadow: tokens.shadow4,
    display: 'flex',
    flexDirection: 'column',
  },
  strip: {
    height: '4px',
    background: tokens.colorNeutralStroke2,
  },
  stripDue: {
    background: tokens.colorPaletteDarkOrangeForeground1,
  },
  stripOverdue: {
    background: tokens.colorPaletteRedForeground1,
  },
  cardHead: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
    minWidth: '240px',
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.3,
  },
  demandId: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  cardBody: {
    padding: tokens.spacingHorizontalL,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(220px, 1fr)',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 800px)': {
      gridTemplateColumns: '1fr',
    },
  },
  cardMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  cardSide: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  sideBlock: {
    background: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sideTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    margin: 0,
  },
  similarItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    cursor: 'pointer',
    ':last-child': { borderBottom: 'none' },
  },
  similarTitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorBrandForegroundLink,
    fontWeight: tokens.fontWeightSemibold,
  },
  similarMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  personCard: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  avatar: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    background: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0,
  },
  personName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  personRole: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  completenessRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  completenessMeter: {
    width: '60px',
    height: '6px',
    background: tokens.colorNeutralBackground4,
    borderRadius: tokens.borderRadiusCircular,
    overflow: 'hidden',
  },
  completenessFill: {
    height: '100%',
    transitionProperty: 'width',
    transitionDuration: '200ms',
  },
  completenessGood: {
    background: tokens.colorPaletteGreenForeground2,
  },
  completenessMedium: {
    background: tokens.colorPaletteDarkOrangeForeground1,
  },
  completenessLow: {
    background: tokens.colorPaletteRedForeground1,
  },
  skillsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  auditHint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    marginLeft: 'auto',
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    width: '100%',
  },
  spacerGrow: {
    flex: 1,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  fieldValue: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.5,
  },
  fieldMuted: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  actions: {
    padding: tokens.spacingHorizontalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  stepLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  stepNum: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground2,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
  },
  stepDone: {
    background: tokens.colorPaletteGreenBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  stepAlert: {
    background: tokens.colorPaletteDarkOrangeForeground1,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
  },
  optional: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  empty: {
    textAlign: 'center',
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalL}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
  },
  emptyIcon: {
    color: tokens.colorPaletteGreenForeground2,
  },
  emptyTitle: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
  },
  emptyMessage: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '420px',
    margin: 0,
  },
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  cardStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
})

/* ─── Filter chips ─────────────────────────────────────────────── */

interface FilterChipsProps {
  current: TriageFilter
  counts: Record<TriageFilter, number>
  onChange: (f: TriageFilter) => void
}

function FilterChips({ current, counts, onChange }: FilterChipsProps) {
  const styles = useStyles()
  const items: Array<{ key: TriageFilter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'new_intake', label: 'New intake' },
    { key: 'awaiting_priority', label: 'Awaiting priority' },
    { key: 'open_no_apps', label: 'Posted · no apps yet' },
  ]
  return (
    <div className={styles.chips} role="tablist" aria-label="Triage filters">
      {items.map((it) => {
        const active = current === it.key
        return (
          <button
            key={it.key}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`triage-chip-${it.key}`}
            className={`${styles.chip} ${active ? styles.chipActive : ''}`}
            onClick={() => onChange(it.key)}
          >
            <span>{it.label}</span>
            <span className={styles.chipCount} data-testid={`triage-count-${it.key}`}>
              {counts[it.key]}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/* ─── Triage actions (new intake / awaiting priority) ──────────── */

interface TriageActionsProps {
  demand: Demand
  onAdvance: (tier: StoredPriorityTierKey | null, rationale: string) => void
  onNotPrioritized: (rationale: string) => void
  onViewDetail: () => void
  busy: boolean
}

function TriageActions({
  demand,
  onAdvance,
  onNotPrioritized,
  onViewDetail,
  busy,
}: TriageActionsProps) {
  const styles = useStyles()
  // Parking Lot is derived from the stage (#988 §7) — the backend 400s a
  // write, and before #1156 this quick-pick offered it anyway, nested the
  // transition inside the PATCH's onSuccess, and lost the rationale when the
  // 400 landed. The writable type + disabled quadrant make that state
  // unrepresentable here.
  const stored = canonicalizeTier(demand.priority_tier_override)
  const initialTier = stored === 'parking-lot' ? null : stored
  const [tier, setTier] = useState<StoredPriorityTierKey | null>(initialTier)
  const [rationale, setRationale] = useState('')
  const canPost = rationale.trim().length > 0
  const canNotPrioritized = rationale.trim().length > 0

  return (
    <>
      <div>
        <div className={styles.stepLabel}>
          <span className={mergeClasses(styles.stepNum, !!tier && styles.stepDone)}>1</span>
          <span>
            Assign priority bucket <span className={styles.optional}>(optional)</span>
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          <QuadrantPicker
            value={tier}
            onChange={(t) => setTier(t === 'parking-lot' ? null : t)}
            disabledKeys={['parking-lot']}
          />
        </div>
      </div>

      <div>
        <div className={styles.stepHeader}>
          <span className={mergeClasses(styles.stepNum, rationale.trim() && styles.stepDone)}>
            2
          </span>
          <span className={styles.stepLabel}>
            Rationale <span className={styles.required}>*</span>
          </span>
          <span className={styles.auditHint} data-testid="audit-hint">
            Visible in audit trail
          </span>
        </div>
        <Textarea
          data-testid="triage-rationale"
          rows={2}
          placeholder="Why this decision? Be specific — this is in the audit log."
          value={rationale}
          onChange={(_e, data) => setRationale(data.value)}
          style={{ width: '100%', marginTop: 8 }}
        />
      </div>

      <div className={styles.actionRow}>
        <Button
          appearance="primary"
          size="large"
          icon={canPost ? <ArrowRight16Regular /> : undefined}
          iconPosition="after"
          disabled={!canPost || busy}
          data-testid="triage-advance"
          onClick={() => {
            onAdvance(tier, rationale.trim())
          }}
        >
          {canPost ? 'Post for Applications' : 'Add rationale to advance'}
        </Button>
        <Button
          appearance="secondary"
          size="large"
          disabled={!canNotPrioritized || busy}
          data-testid="triage-not-prioritized"
          onClick={() => onNotPrioritized(rationale.trim())}
        >
          Parking Lot
        </Button>
        <div className={styles.spacerGrow} />
        <Button
          appearance="subtle"
          icon={<Open16Regular />}
          data-testid={`triage-view-detail-${demand.id}`}
          onClick={onViewDetail}
        >
          View full detail
        </Button>
      </div>
    </>
  )
}

/* ─── Open-no-apps actions ─────────────────────────────────────── */

interface OpenNoAppsActionsProps {
  demand: Demand
  onInvite: () => void
  onNotPrioritized: () => void
  onViewDetail: () => void
  busy: boolean
}

function OpenNoAppsActions({
  demand,
  onInvite,
  onNotPrioritized,
  onViewDetail,
  busy,
}: OpenNoAppsActionsProps) {
  const styles = useStyles()
  const days = daysInStage(demand)
  return (
    <>
      <div>
        <div className={styles.stepLabel}>
          <span className={mergeClasses(styles.stepNum, styles.stepAlert)}>!</span>
          <span>
            This demand has been posted for {days} days with no applicants
          </span>
        </div>
        <Text size={200} style={{ display: 'block', marginTop: 4 }}>
          Council can invite specific talents from the directory, or park it.
        </Text>
      </div>
      <div className={styles.actionRow}>
        <Button
          appearance="primary"
          size="large"
          icon={<People16Regular />}
          disabled={busy}
          data-testid="invite-talents"
          onClick={onInvite}
        >
          Invite specific talents
        </Button>
        <Button
          appearance="secondary"
          size="large"
          disabled={busy}
          data-testid="open-no-apps-not-prioritized"
          onClick={onNotPrioritized}
        >
          Parking Lot (no fit found)
        </Button>
        <div className={styles.spacerGrow} />
        <Button
          appearance="subtle"
          icon={<Open16Regular />}
          data-testid={`triage-view-detail-${demand.id}`}
          onClick={onViewDetail}
        >
          View full detail
        </Button>
      </div>
    </>
  )
}

/* ─── Decision card ────────────────────────────────────────────── */

interface DecisionCardProps {
  demand: Demand
  flavour: 'triage' | 'open_no_apps'
  similar: SimilarDemand[]
  onAdvance: (tier: StoredPriorityTierKey | null, rationale: string) => void
  onNotPrioritized: (rationale: string | null) => void
  onInvite: () => void
  onViewDetail: () => void
  onViewSimilar: (id: number) => void
  busy: boolean
}

function DecisionCard({
  demand,
  flavour,
  similar,
  onAdvance,
  onNotPrioritized,
  onInvite,
  onViewDetail,
  onViewSimilar,
  busy,
}: DecisionCardProps) {
  const styles = useStyles()
  const days = daysInStage(demand)
  const urgency = urgencyForDays(days)
  const stripClass =
    urgency === 'overdue'
      ? styles.stripOverdue
      : urgency === 'due'
        ? styles.stripDue
        : ''

  const badgeColor = urgency === 'overdue' ? 'danger' : urgency === 'due' ? 'warning' : 'informative'

  return (
    <article className={styles.card} data-testid={`decision-card-${demand.id}`}>
      <div className={mergeClasses(styles.strip, stripClass)} />
      <header className={styles.cardHead}>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <h2 className={styles.title}>{demand.title}</h2>
            {demand.demand_id ? (
              <span className={styles.demandId} data-testid={`card-demand-id-${demand.id}`}>
                {demand.demand_id}
              </span>
            ) : null}
          </div>
          <div className={styles.badgeRow}>
            <Badge
              color={badgeColor}
              appearance="filled"
              data-testid={`card-days-${demand.id}`}
            >
              {urgency === 'overdue'
                ? `${days} days overdue`
                : `${days} days waiting`}
            </Badge>
            <Badge appearance="outline" data-testid={`card-fa-${demand.id}`}>
              {focusAreaLabel(demand.focus_area_code)}
            </Badge>
          </div>
        </div>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.cardMain}>
          {demand.issue_problem ? (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>The problem</span>
              <span className={styles.fieldValue}>{demand.issue_problem}</span>
            </div>
          ) : (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>The problem</span>
              <span className={mergeClasses(styles.fieldValue, styles.fieldMuted)}>
                No problem statement captured yet.
              </span>
            </div>
          )}
          {demand.target_user ? (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Target user</span>
              <span className={styles.fieldValue}>{demand.target_user}</span>
            </div>
          ) : null}
          {demand.anticipated_benefit ? (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Anticipated benefit</span>
              <span className={styles.fieldValue}>{demand.anticipated_benefit}</span>
            </div>
          ) : null}
          {demand.business_criticality ? (
            <div className={styles.field} data-testid={`card-criticality-${demand.id}`}>
              <span className={styles.fieldLabel}>Business criticality</span>
              <span className={styles.fieldValue}>{demand.business_criticality}</span>
            </div>
          ) : null}
          {demand.skills && demand.skills.length > 0 ? (
            <div className={styles.field} data-testid={`card-skills-${demand.id}`}>
              <span className={styles.fieldLabel}>Required skills</span>
              <div className={styles.skillsRow}>
                {demand.skills.map((skill) => (
                  <Badge key={skill} appearance="outline" size="small">
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <aside className={styles.cardSide}>
          {similar.length > 0 ? (
            <div className={styles.sideBlock} data-testid={`card-similar-${demand.id}`}>
              <h4 className={styles.sideTitle}>Similar demands</h4>
              {similar.map((s) => (
                <div
                  key={s.id}
                  className={styles.similarItem}
                  role="link"
                  data-testid={`similar-${demand.id}-${s.id}`}
                  {...activatable(() => onViewSimilar(s.id))}
                >
                  <span className={styles.similarTitle}>
                    {s.demand_id ? `${s.demand_id} · ` : ''}
                    {s.title}
                  </span>
                  <span className={styles.similarMeta}>
                    <Badge appearance="outline" size="small">
                      {stageLabel(s.stage)}
                    </Badge>
                    <span>· {s.match}% match</span>
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          <div className={styles.sideBlock} data-testid={`card-requestor-${demand.id}`}>
            <h4 className={styles.sideTitle}>Requestor context</h4>
            {demand.requestor_name || demand.requestor_email ? (
              <div className={styles.personCard}>
                <div className={styles.avatar} aria-hidden="true">
                  {initials(demand.requestor_name ?? demand.requestor_email ?? '')}
                </div>
                <div>
                  <div className={styles.personName}>
                    {demand.requestor_name ?? demand.requestor_email}
                  </div>
                  <div className={styles.personRole}>
                    {demand.department ? `${demand.department}` : 'Department not set'}
                    {/* toLocaleDateString, not the raw ISO string — this line
                        used to read "Submitted 2025-05-09T05:26:00Z". Same
                        format the demand detail header uses. */}
                    {demand.submission_date
                      ? ` · Submitted ${new Date(demand.submission_date).toLocaleDateString()}`
                      : ''}
                  </div>
                </div>
              </div>
            ) : (
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Requestor not captured.
              </Text>
            )}
          </div>
        </aside>
      </div>
      <div className={styles.actions}>
        {flavour === 'open_no_apps' ? (
          <OpenNoAppsActions
            demand={demand}
            onInvite={onInvite}
            onNotPrioritized={() => onNotPrioritized(null)}
            onViewDetail={onViewDetail}
            busy={busy}
          />
        ) : (
          <TriageActions
            demand={demand}
            onAdvance={onAdvance}
            onNotPrioritized={(r) => onNotPrioritized(r)}
            onViewDetail={onViewDetail}
            busy={busy}
          />
        )}
      </div>
    </article>
  )
}

/* ─── Empty state ──────────────────────────────────────────────── */

function CouncilEmpty({ completed }: { completed: number }) {
  const styles = useStyles()
  return (
    <div className={styles.empty} data-testid="triage-empty">
      <CheckmarkCircle48Filled className={styles.emptyIcon} />
      <h2 className={styles.emptyTitle}>All caught up</h2>
      <p className={styles.emptyMessage}>
        {completed > 0
          ? `You triaged ${completed} ${completed === 1 ? 'demand' : 'demands'} this session. New intake will appear here as it arrives.`
          : 'No demands need your attention right now.'}
      </p>
    </div>
  )
}

/* ─── Page ─────────────────────────────────────────────────────── */

export default function CouncilTriage() {
  const styles = useStyles()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const toast = useToast()
  const effectiveUser = useEffectiveUser()

  // Council sees only their own FA; admin/super_admin can follow the
  // workspace-supplied focusArea query param.
  const isAdmin =
    effectiveUser?.role === 'admin' || effectiveUser?.role === 'super_admin'
  const requestedFocusArea = searchParams.get('focusArea') ?? undefined
  const faFilter = isAdmin ? requestedFocusArea : (effectiveUser?.focus_area_code ?? undefined)

  // Pull both relevant stages in one query — under_review + open_to_application.
  // The substrate's useDemands accepts a single stage filter; fetch the
  // entire pool with no stage filter and a generous page size so the
  // page can compute the per-bucket counts client-side.
  //
  // 500 is the backend's hard cap, not a comfortable margin — this used to say
  // it was "plenty for triage", which was a guess with nothing keeping it true.
  // Past 500 the page truncates, so `withheld` below says so out loud rather
  // than letting the bucket counts quietly describe a subset (#1052).
  const { data, isLoading, error, refetch } = useDemands({
    page_size: 500,
    ...(faFilter ? { focus_area: faFilter } : {}),
  })
  const updateDemand = useUpdateDemand()
  const transitionDemand = useTransitionDemand()

  const [filter, setFilter] = useState<TriageFilter>(() =>
    normalizeTriageFilter(searchParams.get('filter')),
  )
  const [completedThisSession, setCompletedThisSession] = useState(0)
  // Unmet stage expectations (#988) reach this screen too. A 409 is not a
  // refusal: the move is still available, the demand is just missing things it
  // was expected to have by now. Both handlers below used to hand that to
  // apiErrorMessage, which keeps only `detail.message` — so the backend's
  // "You can continue anyway" was recited as a red toast while the field list
  // was discarded and nothing offered the continue. That is ticket #19, which
  // was reported against the demand-detail dialog; this screen had the same
  // defect waiting.
  const [stageWarnings, setStageWarnings] = useState<StageWarnings | null>(null)
  const [warnedMove, setWarnedMove] = useState<{
    demandId: number
    body: DemandTransitionInput
    onDone: () => void
  } | null>(null)
  // Captures the number of items in the queue when the session started.
  // This anchors the "X of Y triaged this session" progress denominator
  // so it doesn't drift as items leave the queue. Resets on page reload
  // (per #706 — full session-state persistence is out of scope).
  const [sessionTotal, setSessionTotal] = useState<number | null>(null)

  // TODO(#706 follow-up): prev/next/skip navigation across the queue.
  // The audit notes this is a stack-vs-card-at-a-time UX divergence
  // and is intentionally deferred to a Tier 4 discussion.
  // TODO(#706 follow-up): keyboard shortcuts (← → 1-4 Enter) — also
  // deferred pending the navigation model conversation.

  useEffect(() => {
    setFilter(normalizeTriageFilter(searchParams.get('filter')))
  }, [searchParams])

  const items = useMemo(() => data?.items ?? [], [data])

  // The page asks for page_size: 500 and buckets client-side, and the backend
  // caps page_size at exactly 500 (demands.py — `Query(50, ge=1, le=500)`).
  // Past that the queue silently truncates: the buckets, the stats and the
  // "N of M triaged" progress are all computed from `items`, so they would
  // describe the first 500 demands while reading as if they described the
  // queue. Say so instead of quietly being wrong.
  const withheld = Math.max(0, (data?.total ?? 0) - items.length)

  const buckets = useMemo(() => {
    const newIntake: Demand[] = []
    const awaitingPriority: Demand[] = []
    const openNoApps: Demand[] = []
    for (const d of items) {
      if (isNewIntake(d)) newIntake.push(d)
      else if (isAwaitingPriority(d)) awaitingPriority.push(d)
      else if (isOpenNoApps(d)) openNoApps.push(d)
    }
    // Sort each bucket by days waiting descending so the most urgent
    // floats to the top.
    const byDaysDesc = (a: Demand, b: Demand) =>
      daysInStage(b) - daysInStage(a)
    newIntake.sort(byDaysDesc)
    awaitingPriority.sort(byDaysDesc)
    openNoApps.sort(byDaysDesc)
    return { newIntake, awaitingPriority, openNoApps }
  }, [items])

  const counts: Record<TriageFilter, number> = useMemo(
    () => ({
      all:
        buckets.newIntake.length +
        buckets.awaitingPriority.length +
        buckets.openNoApps.length,
      overdue:
        buckets.newIntake.filter((d) => isOverdueDecision(d)).length +
        buckets.awaitingPriority.filter((d) => isOverdueDecision(d)).length,
      new_intake: buckets.newIntake.length,
      awaiting_priority: buckets.awaitingPriority.length,
      open_no_apps: buckets.openNoApps.length,
    }),
    [buckets],
  )

  const visible: Array<{ demand: Demand; flavour: 'triage' | 'open_no_apps' }> = useMemo(() => {
    const triageEntry = (d: Demand) => ({ demand: d, flavour: 'triage' as const })
    const onaEntry = (d: Demand) => ({ demand: d, flavour: 'open_no_apps' as const })
    if (filter === 'overdue') {
      return [...buckets.newIntake, ...buckets.awaitingPriority]
        .filter((d) => isOverdueDecision(d))
        .map(triageEntry)
    }
    if (filter === 'new_intake') return buckets.newIntake.map(triageEntry)
    if (filter === 'awaiting_priority') return buckets.awaitingPriority.map(triageEntry)
    if (filter === 'open_no_apps') return buckets.openNoApps.map(onaEntry)
    return [
      ...buckets.newIntake.map(triageEntry),
      ...buckets.awaitingPriority.map(triageEntry),
      ...buckets.openNoApps.map(onaEntry),
    ]
  }, [filter, buckets])

  // Anchor the session denominator the first time we see a non-zero
  // queue so the progress bar reads "X of Y triaged" against a stable
  // Y until the next page reload.
  useEffect(() => {
    if (sessionTotal === null && counts.all > 0) {
      setSessionTotal(counts.all)
    }
  }, [sessionTotal, counts.all])
  const denom = sessionTotal !== null ? Math.max(sessionTotal, completedThisSession) : counts.all
  const progressPct = denom > 0 ? Math.min(100, (completedThisSession / denom) * 100) : 0

  // Stats row counters — derived from the actively-queued items only.
  const queued: Demand[] = useMemo(
    () => [...buckets.newIntake, ...buckets.awaitingPriority, ...buckets.openNoApps],
    [buckets],
  )
  const stats = useMemo(() => {
    let overdue = 0
    let due = 0
    for (const d of queued) {
      const u = urgencyForDays(daysInStage(d))
      if (u === 'overdue') overdue += 1
      else if (u === 'due') due += 1
    }
    return { overdue, due, total: queued.length }
  }, [queued])

  // Build the once-per-render similar-demand index from the full pool.
  // Memoised against items so a re-render of the page doesn't recompute
  // for every card.
  const similarByDemand = useMemo(() => {
    const map = new Map<number, SimilarDemand[]>()
    for (const d of queued) {
      map.set(d.id, findSimilarDemands(d, items))
    }
    return map
  }, [queued, items])

  const handleAdvance = useCallback(
    (demand: Demand, tier: StoredPriorityTierKey | null, rationale: string) => {
      // Step 1: persist the chosen priority + rationale (skip priority if not set).
      // Step 2: advance one step. Resourcing is reachable only via Triaging
      // (v2.8.0), so a Scoping card advances to Triaging; a Triaging card posts
      // for applications (→ Resourcing).
      const toStage = demand.stage === 'scoping' ? 'triaging' : 'resourcing'
      const advancedLabel = toStage === 'resourcing' ? 'Posted for applications' : 'Moved to Triaging'
      const data: Record<string, string> = { priority_override_reason: rationale }
      if (tier) data.priority_tier_override = tier
      updateDemand.mutate(
        {
          id: demand.id,
          data,
        },
        {
          onSuccess: () => {
            transitionDemand.mutate(
              {
                id: demand.id,
                body: { to_stage: toStage, notes: rationale },
              },
              {
                onSuccess: () => {
                  setCompletedThisSession((n) => n + 1)
                  toast.showToast({
                    title: advancedLabel,
                    body: demand.title,
                    intent: 'success',
                  })
                },
                onError: (err) => {
                  const warnings = parseStageWarnings(err)
                  if (warnings) {
                    setStageWarnings(warnings)
                    setWarnedMove({
                      demandId: demand.id,
                      body: { to_stage: toStage, notes: rationale },
                      onDone: () => {
                        setCompletedThisSession((n) => n + 1)
                        toast.showToast({
                          title: advancedLabel,
                          body: demand.title,
                          intent: 'success',
                        })
                      },
                    })
                    return
                  }
                  // The priority PATCH above already committed — only the move
                  // failed. Saying "could not advance" alone reads as "nothing
                  // happened" and sends people to re-enter a rationale that is
                  // already saved (#1155).
                  toast.showToast({
                    title: 'Priority saved, but the demand did not move',
                    body: apiErrorMessage(err, 'Server rejected the transition'),
                    intent: 'error',
                  })
                },
              },
            )
          },
          onError: (err) => {
            toast.showToast({
              title: 'Could not save priority',
              body: apiErrorMessage(err, 'Server rejected the update'),
              intent: 'error',
            })
          },
        },
      )
    },
    [updateDemand, transitionDemand, toast],
  )

  const handleNotPrioritized = useCallback(
    (demand: Demand, rationale: string | null) => {
      // Parking Lot is DERIVED from the stage now (#988 §7), so there is no
      // bucket to write alongside the transition — moving the stage IS the
      // whole operation. The old version PATCHed 'parking-lot' first and
      // transitioned in its onSuccess: two untransacted requests, and the
      // backend now refuses that write outright.
      const body = rationale
        ? { to_stage: 'parking' as const, notes: rationale }
        : { to_stage: 'parking' as const }
      transitionDemand.mutate(
        { id: demand.id, body },
        {
          onSuccess: () => {
            setCompletedThisSession((n) => n + 1)
            toast.showToast({
              title: 'Moved to Parking Lot',
              body: demand.title,
              intent: 'success',
            })
          },
          onError: (err) => {
            const warnings = parseStageWarnings(err)
            if (warnings) {
              setStageWarnings(warnings)
              setWarnedMove({
                demandId: demand.id,
                body,
                onDone: () => {
                  setCompletedThisSession((n) => n + 1)
                  toast.showToast({
                    title: 'Moved to Parking Lot',
                    body: demand.title,
                    intent: 'success',
                  })
                },
              })
              return
            }
            toast.showToast({
              title: 'Could not shelve demand',
              body: apiErrorMessage(err, 'Server rejected the transition'),
              intent: 'error',
            })
          },
        },
      )
    },
    [transitionDemand, toast],
  )

  const handleInvite = useCallback(
    (demand: Demand) => {
      navigate(`/talents?inviteFor=${demand.id}`)
    },
    [navigate],
  )

  const handleViewDetail = useCallback(
    (demand: Demand) => {
      navigate(`/demand/${demand.id}`)
    },
    [navigate],
  )

  const handleViewSimilar = useCallback(
    (id: number) => {
      navigate(`/demand/${id}`)
    },
    [navigate],
  )

  /* ─── Render ──────────────────────────────────────────────── */

  if (isLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner label="Loading the triage queue…" />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorCard
        title="Could not load triage queue"
        message={apiErrorMessage(error, 'Unknown error')}
        retry={() => {
          void refetch()
        }}
      />
    )
  }

  const mutating = updateDemand.isPending || transitionDemand.isPending

  return (
    <div className={styles.page} data-testid="council-triage-page">
      {withheld > 0 && (
        <MessageBar intent="warning" data-testid="triage-truncated">
          <MessageBarBody>
            Showing the {items.length} most recent demands of {data?.total}. The counts and
            progress below describe only those — {withheld} more are not on this page. Filter by
            focus area to narrow the queue.
          </MessageBarBody>
        </MessageBar>
      )}
      <div className={styles.header}>
        <div className={styles.headerTitleBlock}>
          <Text size={600} weight="semibold">
            Decisions waiting for you
          </Text>
          <Text size={300}>
            Triage incoming demands, set priority buckets, and unblock posted-but-quiet work.
          </Text>
          <div className={styles.progressRow} data-testid="triage-progress">
            <span className={styles.progressLabel}>
              {completedThisSession} of {denom} triaged this session
            </span>
            <div
              className={styles.progressBar}
              role="progressbar"
              aria-valuenow={Math.round(progressPct)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className={styles.progressFill}
                style={{ width: `${progressPct}%` }}
                data-testid="triage-progress-fill"
              />
            </div>
          </div>
        </div>
        <div className={styles.stats} data-testid="triage-stats" role="group" aria-label="Queue stats">
          <div className={styles.stat}>
            <span
              className={mergeClasses(styles.statNum, styles.statNumDanger)}
              data-testid="triage-stat-overdue"
            >
              {stats.overdue}
            </span>
            <span className={styles.statLabel}>Overdue</span>
          </div>
          <div className={styles.stat}>
            <span
              className={mergeClasses(styles.statNum, styles.statNumWarning)}
              data-testid="triage-stat-due"
            >
              {stats.due}
            </span>
            <span className={styles.statLabel}>Due soon</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statNum} data-testid="triage-stat-total">
              {stats.total}
            </span>
            <span className={styles.statLabel}>Total left</span>
          </div>
        </div>
      </div>

      <FilterChips current={filter} counts={counts} onChange={setFilter} />

      {visible.length === 0 ? (
        <CouncilEmpty completed={completedThisSession} />
      ) : (
        <div className={styles.cardStack}>
          {visible.map(({ demand, flavour }) => (
            <DecisionCard
              key={demand.id}
              demand={demand}
              flavour={flavour}
              similar={similarByDemand.get(demand.id) ?? []}
              busy={mutating}
              onAdvance={(tier, rationale) => handleAdvance(demand, tier, rationale)}
              onNotPrioritized={(rationale) => handleNotPrioritized(demand, rationale)}
              onInvite={() => handleInvite(demand)}
              onViewDetail={() => handleViewDetail(demand)}
              onViewSimilar={handleViewSimilar}
            />
          ))}
        </div>
      )}

      <StageWarningDialog
        warnings={stageWarnings}
        toStage={warnedMove?.body.to_stage ?? null}
        busy={mutating}
        onContinue={(acknowledgeWith) => {
          if (!warnedMove) return
          const { demandId, body, onDone } = warnedMove
          setStageWarnings(null)
          transitionDemand.mutate(
            { id: demandId, body: { ...body, acknowledged_warnings: acknowledgeWith } },
            {
              onSuccess: () => {
                setWarnedMove(null)
                onDone()
              },
              onError: (err) => {
                setWarnedMove(null)
                toast.showToast({
                  title: 'Could not move demand',
                  body: apiErrorMessage(err, 'Server rejected the transition'),
                  intent: 'error',
                })
              },
            },
          )
        }}
        onGoFix={() => {
          setStageWarnings(null)
          setWarnedMove(null)
        }}
      />
    </div>
  )
}
